import { CHAT_ATTACHMENT_FAILURE_REASON, CHAT_ATTACHMENT_STATUS } from './constants';
import { AttachmentChipView, ChatAttachmentChip, ClaimedAttachment, SentAttachmentFile } from './types';

/** Maps one claim-response / final-frame attachment entry onto the chip fields it determines. */
export const toChipState = (
    attachment: ClaimedAttachment,
    getCompanyName?: (orgId: string | null) => string | undefined,
): Pick<AttachmentChipView, 'status' | 'companyName' | 'failureReason'> => ({
    status: attachment.status === 'accepted' ? CHAT_ATTACHMENT_STATUS.READY : attachment.status,
    companyName: attachment.org_name ?? getCompanyName?.(attachment.org_id),
    failureReason: attachment.failure_reason ?? undefined,
});

/** A read-only chip for a file on an already-sent message. */
export const toSentChip = (attachment: ClaimedAttachment): AttachmentChipView => ({
    id: attachment.mnemosyne_doc_id,
    fileName: attachment.file_name,
    ...toChipState(attachment),
});

const FAILURES_WITH_DOWNLOADABLE_ROW: ReadonlySet<string> = new Set(Object.values(CHAT_ATTACHMENT_FAILURE_REASON));

/** True when the server wrote a row for this entry, so a download link resolves; every other drop (e.g. NOT_YOUR_UPLOAD) has none and stays a chip. */
export const hasDownloadableRow = (attachment: ClaimedAttachment): boolean => {
    switch (attachment.status) {
        case CHAT_ATTACHMENT_STATUS.FILED:
        case CHAT_ATTACHMENT_STATUS.NEEDS_COMPANY:
            return true;
        case CHAT_ATTACHMENT_STATUS.FAILED:
            return !!attachment.failure_reason && FAILURES_WITH_DOWNLOADABLE_ROW.has(attachment.failure_reason);
        default:
            return false;
    }
};

export const SENT_ATTACHMENT_RENDER = { CARD: 'card', PENDING_CARD: 'pending-card', CHIP: 'chip' } as const;
export type SentAttachmentRender = (typeof SENT_ATTACHMENT_RENDER)[keyof typeof SENT_ATTACHMENT_RENDER];

/** The ONLY place a sent entry's rendering is decided. `hasDownloadableRow` still owns "is there a row";
 *  PENDING_CARD is the client-only pre-verdict state and is deliberately NOT downloadable (no row yet),
 *  so it renders without an actions menu. */
export const sentAttachmentRender = (attachment: ClaimedAttachment): SentAttachmentRender => {
    if (hasDownloadableRow(attachment)) return SENT_ATTACHMENT_RENDER.CARD;
    if (attachment.status === CHAT_ATTACHMENT_STATUS.PROCESSING) return SENT_ATTACHMENT_RENDER.PENDING_CARD;
    return SENT_ATTACHMENT_RENDER.CHIP;
};

/** The composer's settled chips, shaped like a final-frame entry so one renderer serves both.
 *  `processing` is never a wire status — Athena emits accepted/filed/needs_company/failed/deleted —
 *  so it unambiguously means "sent, no server verdict yet". */
export const toPendingUpload = (chip: ChatAttachmentChip): ClaimedAttachment => ({
    mnemosyne_doc_id: chip.mnemosyneDocId as string,
    file_name: chip.fileName,
    status: CHAT_ATTACHMENT_STATUS.PROCESSING,
    org_id: null,
    org_name: null,
    failure_reason: null,
});

/** A sent entry whose `message_attachments` row the server has CONFIRMED it wrote. The turn's
 *  first SSE frame carries `user_message_id`, and Athena stamps that only once the attachment
 *  rows have committed, so the row-written rule is satisfied by construction — the same argument
 *  `toHistorySentAttachment` makes. `needs_company` is the weaker of the two statuses
 *  `hasDownloadableRow` accepts: it asserts the row, not a filing. The final frame then replaces
 *  the entry with the server's own verdict. */
export const toRowWrittenUpload = (attachment: ClaimedAttachment): ClaimedAttachment => ({
    ...attachment,
    status: CHAT_ATTACHMENT_STATUS.NEEDS_COMPANY,
});

/** History's user-upload entries. Each one exists BECAUSE a message_attachments row exists,
 *  so the row-written rule is satisfied by construction, not by trusting a status the history
 *  payload does not carry. */
export const toHistorySentAttachment = (file: SentAttachmentFile): ClaimedAttachment => ({
    mnemosyne_doc_id: file.mnemosyne_doc_id as string,
    file_name: file.file_name,
    status: CHAT_ATTACHMENT_STATUS.FILED,
    org_id: null,
    org_name: null,
    failure_reason: null,
});
